<template>
  <div class="applications-filter">
    <slot name="activator"></slot>
    <v-card v-if="!empty">
      <v-card-text>
        <span> Applied Filters </span>
        <v-chip
          v-for="(filter, key) in appliedFilters"
          :key="key"
          @input="clearFilter(key)"
          clipped
          label
          small
          close
        >
          {{ toSentenceCase(key) }}
        </v-chip>
      </v-card-text>
    </v-card>

    <v-navigation-drawer
      fixed
      app
      right
      width="600"
      disable-resize-watcher
      overflow
      @input="toggleShow"
      :value="show"
      class="drawer"
    >
      <v-card flat>
        <v-card-title class="pb-0">
          <v-spacer />
          <v-icon @click="toggleShow(false)" style="cursor: pointer;">mdi-close</v-icon>
        </v-card-title>

        <v-card-text class="text-xs-center py-0">
          <v-layout column fill-height>
            <v-flex>
              <v-card-text class="title"> Applications Filter </v-card-text>
            </v-flex>
            <v-btn block outline @click="reset" :disabled="empty">
              <v-icon left>mdi-filter-remove</v-icon>Clear Filters
            </v-btn>
            <v-flex>
              <v-btn
                @click="
                  () => {
                    filters.complete = false
                    filters.referred = false
                    submitSearch()
                  }
                "
                :flat="filters.complete || filters.referred"
                >All applications</v-btn
              >
              <v-btn
                @click="
                  () => {
                    filters.complete = true
                    filters.referred = false
                    submitSearch()
                  }
                "
                :flat="filters.complete !== true"
              >
                Only completed</v-btn
              >
              <v-btn
                @click="
                  () => {
                    filters.referred = true
                    filters.complete = false
                    submitSearch()
                  }
                "
                :flat="filters.referred !== true"
              >
                Only referred</v-btn
              >
            </v-flex>
          </v-layout>
        </v-card-text>

        <v-card-text>
          <v-form>
            <v-expansion-panel>
              <v-expansion-panel-content key="profile-requirements-panel">
                <div slot="header">
                  <div>
                    <v-icon class="text-vertical-middle" small>mdi-account</v-icon>&nbsp;
                    <span>Profile requirements</span>
                  </div>
                </div>
                <v-card>
                  <v-card-text>
                    <v-text-field
                      label="Search by name"
                      color="primary"
                      hint="e.g. Linguina Nettlewater"
                      @keypress.enter="submitSearch"
                      v-model="filters.name"
                      @input="submitSearch"
                      hide-details
                      clearable
                    >
                    </v-text-field>

                    <v-autocomplete
                      label="Filter by ethnicity"
                      color="primary"
                      append-icon="mdi-chevron-down"
                      v-model="filters.ethnicities"
                      @input="submitSearch"
                      :items="ethnicities"
                      hide-details
                      clearable
                      multiple
                      attach
                      dense
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-account-group</v-icon>
                          <div>
                            {{ item }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>

                    <v-autocomplete
                      label="Filter by gender"
                      :items="['Male', 'Female', 'Other']"
                      color="primary"
                      append-icon="mdi-chevron-down"
                      v-model="filters.genders"
                      @input="submitSearch"
                      hide-details
                      clearable
                      multiple
                      attach
                      dense
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-human-male-female</v-icon>
                          <div>
                            {{ item }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>

                    <v-autocomplete
                      label="Filter by opportunity location"
                      color="primary"
                      append-icon="mdi-chevron-down"
                      v-model="filters.locations"
                      @input="submitSearch"
                      item-text="address"
                      :items="locations"
                      item-value="id"
                      hide-details
                      clearable
                      multiple
                      attach
                      dense
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-account-group</v-icon>
                          <div>
                            {{ item.address }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>

                    <LocationSelect
                      label="Filter by physical address"
                      v-model="filters.location"
                      @blur="submitSearch"
                      @keyup.enter="submitSearch"
                      coordinates
                      hide-details
                      clearable
                      attach
                      dense
                      :toFront="false"
                    >
                      <template slot="selection" slot-scope="{ location }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-map-marker</v-icon>
                          <div>
                            {{ location.address }}
                          </div>
                        </div>
                      </template>
                    </LocationSelect>
                    <v-slider
                      v-if="filters.location"
                      label="Distance"
                      v-model="filters.distance"
                      :min="0"
                      :max="600"
                      thumb-label
                      color="primary"
                      step="5"
                      always-dirty
                      :hint="`Within ${filters.distance} Km`"
                      :persistent-hint="!!filters.distance"
                      @input="submitSearch"
                    />
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>

              <v-expansion-panel-content key="qualification-requirements-panel">
                <div slot="header">
                  <div>
                    <v-icon class="text-vertical-middle" small>mdi-school</v-icon>&nbsp;
                    <span>Qualification requirements</span>
                  </div>
                </div>
                <v-card>
                  <v-card-text>
                    <v-switch
                      v-model="filters.verified"
                      label="Verified qualifications"
                      @change="submitSearch"
                      color="primary"
                      class="mt-0"
                      hide-details
                    />
                    <v-switch
                      v-model="filters.include_completed"
                      label="Completed qualifications"
                      @change="submitSearch"
                      color="primary"
                      hide-details
                    />
                    <v-switch
                      v-model="filters.include_current"
                      label="Current qualifications"
                      @change="submitSearch"
                      color="primary"
                      hide-details
                    />
                    <v-switch
                      v-model="filters.include_intended"
                      label="Intended qualifications"
                      @change="submitSearch"
                      color="primary"
                    />

                    <v-label>Filter by academic results</v-label>
                    <v-range-slider
                      v-if="!filters.grade || filters.grade.length == 0"
                      :value="[0, 100]"
                      @input="setGrade"
                      thumb-label
                      :thumb-size="24"
                      ticks="always"
                      step="5"
                      key="passive-grade-filter"
                    />
                    <v-range-slider
                      v-else
                      v-model="filters.grade"
                      @input="submitSearch"
                      thumb-label
                      :thumb-size="24"
                      ticks="always"
                      step="5"
                      key="active-grade-filter"
                    />

                    <v-autocomplete
                      color="primary"
                      append-icon="mdi-chevron-down"
                      v-model="filters.institutions"
                      :items="options.institutions"
                      label="Filter by institution"
                      @input="submitSearch"
                      item-text="name"
                      item-value="id"
                      hide-details
                      clearable
                      multiple
                      attach
                      dense
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-bank</v-icon>
                          <div>
                            {{ item.name }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>

                    <v-autocomplete
                      label="Filter by category"
                      color="primary"
                      append-icon="mdi-chevron-down"
                      v-model="filters.categories"
                      :items="options.categories"
                      @input="submitSearch"
                      item-text="name"
                      item-value="id"
                      hide-details
                      clearable
                      multiple
                      attach
                      dense
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-school</v-icon>
                          <div>
                            {{ item.name }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>

                    <v-autocomplete
                      label="Filter by type"
                      color="primary"
                      append-icon="mdi-chevron-down"
                      v-model="filters.types"
                      :items="options.types"
                      @input="submitSearch"
                      item-text="name"
                      item-value="id"
                      clearable
                      hide-details
                      multiple
                      attach
                      dense
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-certificate</v-icon>
                          <div>
                            {{ item.name }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>

                    <v-autocomplete
                      label="Filter by discipline"
                      color="primary"
                      append-icon="mdi-chevron-down"
                      v-model="filters.disciplines"
                      :items="options.disciplines"
                      @input="submitSearch"
                      item-text="name"
                      item-value="id"
                      hide-details
                      clearable
                      multiple
                      dense
                      attach
                    >
                      <template slot="selection" slot-scope="{ item }">
                        <div class="caption selection-chip">
                          <v-icon small left>mdi-lightbulb</v-icon>
                          <div>
                            {{ item.name }}
                          </div>
                        </div>
                      </template>
                    </v-autocomplete>
                  </v-card-text>
                </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-form>
        </v-card-text>
      </v-card>
    </v-navigation-drawer>
  </div>
</template>

<script>
// Components
import LocationSelect from '@/components/selects/LocationSelect'

// Data
import ethnicities from '@/data/ethnicities'

// Helpers
import { mapState } from 'vuex'
import _ from 'lodash'

// Mixins
import { caseConverters } from '@/mixins/caseConverters.js'
import { managedQueryParameters } from '@/mixins/managedQueryParameters.js'

export default {
  mixins: [caseConverters, managedQueryParameters],

  components: { LocationSelect },

  model: {
    prop: 'show',
    event: 'input',
  },

  props: { show: Boolean },

  created() {
    this.$http
      .get(`admin/opportunities/${this.$route.params.id}/institutions`)
      .then(response => (this.options.institutions = response.data))
    this.$http
      .get(`admin/opportunities/${this.$route.params.id}/categories`)
      .then(response => (this.options.categories = response.data))
    this.$http
      .get(`admin/opportunities/${this.$route.params.id}/types`)
      .then(response => (this.options.types = response.data))
    this.$http
      .get(`admin/opportunities/${this.$route.params.id}/disciplines`)
      .then(response => (this.options.disciplines = response.data))
    this.initializeFilters()
    if (!this.empty) {
      this.$emit('input', !this.show)
      this.applyFilter()
    }
  },

  data() {
    return {
      filters: {
        complete: false,
        referred: false,
        categories: [],
        disciplines: [],
        ethnicities: [],
        genders: [],
        grade: [],
        institutions: [],
        locations: [], // Applied opportunity locations
        name: '',
        types: [],
        location: '', // Candidate location
        distance: '', // and distance tolerance
        verified: false,
        include_current: false,
        include_completed: false,
        include_intended: false,
      },
      options: {
        categories: [],
        disciplines: [],
        institutions: [],
        types: [],
      },
    }
  },

  computed: {
    ...mapState('admin/applications', {
      storeFilters: 'filters',
    }),

    opportunity() {
      return this.$store.getters['admin/opportunities/find']()
    },

    ethnicities() {
      let options = JSON.parse(JSON.stringify(ethnicities))
      return [...[...options.filter(o => o !== 'Other'), 'Indian/Asian'].sort(), 'Other']
    },

    locations() {
      if (_.get(this.opportunity, 'locations.length') > 0) {
        return this.opportunity.locations.map(l => ({ id: l.id, address: l.address }))
      }
      return []
    },

    appliedFilters() {
      let appliedFilters = {}

      Object.keys(this.filters)
        .filter(key => {
          return (
            this.storeFilters[key] &&
            (this.storeFilters[key].length > 0 || this.storeFilters[key] === true)
          )
        })
        .forEach(key => (appliedFilters[key] = this.storeFilters[key]))

      return appliedFilters
    },

    empty() {
      return !Object.keys(this.filters).some(f => {
        return (
          this.filters[f] &&
          (this.filters[f].length > 0 ||
            typeof this.filters[f] === 'number' ||
            this.filters[f] === true)
        )
      })
    },
  },

  methods: {
    toggleShow(e) {
      this.$emit('input', e)
    },

    reset() {
      Object.keys(this.filters).map(f => {
        if (this.filters[f] === true) {
          this.filters[f] = false
        } else if (Number(this.filters[f])) {
          // All the numbers are strings
          this.filters[f] = ''
        } else if (this.filters[f].length > 0) {
          this.filters[f] = []
        }
      })
      this.applyFilter()
    },

    submitSearch: _.debounce(function() {
      this.applyFilter()
    }, 400),

    applyFilter() {
      this.updateQueryParameters({ ...this.filters })
      this.$store.dispatch('admin/applications/updateFilters', this.filters)
    },

    clearFilter(filter) {
      if (this.filters[filter] == true) this.filters[filter] = false
      if (Number(this.filters[filter])) {
        this.filters[filter] = ''
      }
      if (this.filters[filter].length > 0) this.filters[filter] = []
      this.updateQueryParameters({ ...this.filters })
      this.$store.dispatch('admin/applications/updateFilters', this.filters)
    },

    initializeFilters() {
      Object.keys(this.filters).forEach(f => {
        // No value
        if (!this.initialQueryParameters[f]) {
          return
        }

        // Filter type is array and query param is array
        if (
          typeof this.filters[f] === 'object' &&
          typeof this.initialQueryParameters[f] === 'object'
        ) {
          this.filters[f] = this.initialQueryParameters[f].map(param =>
            Number(param) ? Number(param) : param
          )
          return
        }

        // Filter type is array and query param is string
        if (
          typeof this.filters[f] === 'object' &&
          typeof this.initialQueryParameters[f] === 'string'
        ) {
          this.filters[f].push(
            Number(this.initialQueryParameters[f])
              ? Number(this.initialQueryParameters[f])
              : this.initialQueryParameters[f]
          )
          return
        }

        // Filter type is string and query param is array
        if (
          typeof this.filters[f] === 'string' &&
          typeof this.initialQueryParameters[f] === 'object'
        ) {
          this.filters[f] = Number(this.initialQueryParameters[f][0])
            ? Number(this.initialQueryParameters[f][0])
            : this.initialQueryParameters[f][0]
          return
        }

        // Filter type is boolean and query param is array
        if (
          typeof this.filters[f] === 'boolean' &&
          typeof this.initialQueryParameters[f] === 'object'
        ) {
          this.filters[f] = !!this.initialQueryParameters[f][0]
          return
        }

        // Other
        this.filters[f] = this.initialQueryParameters[f]
      })
    },
    setGrade() {
      this.filters.grade = [0, 100]
      this.submitSearch()
    },
  },
}
</script>

<style>
.applications-filter .selection-chip {
  display: table;
  margin: 0.2rem 0;
  vertical-align: middle;
}
.applications-filter .selection-chip * {
  display: table-cell;
  vertical-align: middle;
  padding: 0.3rem 0.5rem;
}

.applications-filter .v-input__slot i {
  cursor: pointer;
}

.applications-filter .drawer {
  padding-bottom: 100px;
}
</style>
